import React, { useContext, useEffect, useMemo, useState } from 'react';
import { CheckCircleFilled } from '@ant-design/icons';
import { Alert, Button, Form, Input, Select, Space } from 'antd';

import ApiConnectionsContext from '@totem/components/administration/apiConnections/ApiConnectionsContext';
import {
  AirwallGroup,
  AirwallTag,
  TemperedConnectionTestRequest,
} from '@totem/components/administration/apiConnections/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import colors from '@totem/styles/colors';
import {
  TEMPERED_AIRWALL_GROUPS_ENDPOINT,
  TEMPERED_AIRWALL_TAGS_ENDPOINT,
  TEMPERED_CONNECTION_TEST_ENDPOINT,
} from '@totem/utilities/endpoints';

const TemperedConductorFields = () => {
  const form = Form.useFormInstance();
  const { getApiToken } = useContext(UserProfileContext);
  const { typeFields } = useContext(ApiConnectionsContext);

  const defaultAirwallTag = useMemo(() => {
    const match = typeFields.find((tfd) => tfd.type === 'Tempered_Conductor');

    return match?.fields.find((fld) => fld.key === 'audience')?.default;
  }, [typeFields]);

  const [testing, setTesting] = useState<boolean>(false);
  const [tested, setTested] = useState<boolean>(false);
  const [testError, setTestError] = useState<string | null>(null);
  const [airwallGroups, setAirwallGroups] = useState<AirwallGroup[]>([]);
  const [airwallTags, setAirwallTags] = useState<AirwallTag[]>([]);

  const baseUrl: string = Form.useWatch('baseUrl', form);
  const clientId: string = Form.useWatch('clientId', form);
  const token: string = Form.useWatch('token', form);
  const savedGroup: string = Form.useWatch('group', form);
  const savedTag: string = Form.useWatch('audience', form);

  // On edit, seed the selects with the already-saved value so it displays
  // immediately; a fresh Test Connection replaces this with the live list.
  // Depends on the watched values (not just mount) since Form.useWatch can
  // still be catching up to a just-applied setFieldsValue on first render.
  useEffect(() => {
    if (savedGroup) {
      setAirwallGroups((current) =>
        current.length === 0
          ? [
              {
                id: savedGroup,
                uuid: '',
                name: savedGroup,
                description: '',
                enabled: true,
              },
            ]
          : current,
      );
    }
  }, [savedGroup]);

  useEffect(() => {
    if (savedTag) {
      setAirwallTags((current) =>
        current.length === 0
          ? [{ id: savedTag, name: savedTag, ownership: '' }]
          : current,
      );
    }
  }, [savedTag]);

  const canTest = !!baseUrl && !!clientId && !!token;

  const handleTest = () => {
    if (testing) {
      return;
    }

    setTesting(true);

    setTestError(null);

    const body: TemperedConnectionTestRequest = { baseUrl, clientId, token };
    const headers = new Headers({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getApiToken()}`,
    });

    Promise.all([
      fetch(TEMPERED_CONNECTION_TEST_ENDPOINT, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      }),
      fetch(TEMPERED_AIRWALL_GROUPS_ENDPOINT, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      }),
      fetch(TEMPERED_AIRWALL_TAGS_ENDPOINT, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      }),
    ])
      .then(async ([testRes, groupsRes, tagsRes]) => {
        if (!testRes.ok) {
          throw new Error('Unable to connect with the provided credentials.');
        }

        const [groups, tags]: [AirwallGroup[], AirwallTag[]] =
          await Promise.all([groupsRes.json(), tagsRes.json()]);

        setAirwallGroups(groups ?? []);

        setAirwallTags(tags ?? []);

        // Default to the configured tag when nothing's already selected (a
        // fresh connection, not one being re-tested after editing).
        if (defaultAirwallTag && !form.getFieldValue('audience')) {
          const defaultTag = (tags ?? []).find(
            (tag) => tag.name === defaultAirwallTag,
          );

          if (defaultTag) {
            form.setFieldValue('audience', defaultTag.name);
          }
        }

        form.setFieldValue('confirmed', true);

        setTested(true);
      })
      .catch((err) => {
        form.setFieldValue('confirmed', false);

        setTested(false);

        setTestError(err?.message ?? 'Connection test failed.');
      })
      .finally(() => setTesting(false));
  };

  const clearTested = () => {
    if (tested) {
      setTested(false);

      form.setFieldValue('confirmed', false);
    }
  };

  return (
    <>
      <Form.Item
        name="baseUrl"
        label="Base URL"
        rules={[{ required: true, message: 'Base URL is required' }]}
      >
        <Input
          placeholder="Ex: <client>.tempered.network"
          onChange={clearTested}
        />
      </Form.Item>
      <Form.Item
        name="clientId"
        label="Client ID"
        rules={[{ required: true, message: 'Client ID is required' }]}
      >
        <Input onChange={clearTested} />
      </Form.Item>
      <Form.Item
        name="token"
        label="Token"
        rules={[{ required: true, message: 'Token is required' }]}
      >
        <Input.Password onChange={clearTested} />
      </Form.Item>
      <Form.Item
        name="partnerName"
        label="Profile Name"
        rules={[{ required: true, message: 'Profile Name is required' }]}
      >
        <Input />
      </Form.Item>
      <Form.Item>
        <Space>
          <Button onClick={handleTest} loading={testing} disabled={!canTest}>
            Test Connection
          </Button>
          {tested && (
            <span>
              <CheckCircleFilled
                style={{ color: colors.utility.positive, marginRight: 6 }}
              />
              Connected
            </span>
          )}
        </Space>
      </Form.Item>
      {testError && (
        <Alert type="error" message={testError} style={{ marginBottom: 16 }} />
      )}
      <Form.Item
        name="group"
        label="Airwall Group"
        rules={[{ required: true, message: 'Airwall Group is required' }]}
      >
        <Select
          disabled={airwallGroups.length === 0}
          placeholder="Test the connection to choose a group"
          options={airwallGroups.map((group) => ({
            value: group.id,
            label: group.name,
          }))}
        />
      </Form.Item>
      <Form.Item
        name="audience"
        label="Airwall Tag"
        rules={[{ required: true, message: 'Airwall Tag is required' }]}
      >
        <Select
          disabled={airwallTags.length === 0}
          placeholder="Test the connection to choose a tag"
          options={airwallTags.map((tag) => ({
            value: tag.name,
            label: tag.name,
          }))}
        />
      </Form.Item>
    </>
  );
};

export default TemperedConductorFields;
